import React from 'react'
import { NonIndexRouteObject, Navigate } from 'react-router-dom'
import Root from '@/root'
import * as pages from '@/pages'
import PublicAuthLayout from '@/layout/PublicAuth'
import { AuthComponent } from '@/layout/Auth'
import Empty, { ErrorPage } from '@/layout/Fallback'
import { ImageIcon } from '@/icon'
import org_menu_icon from '@/asset/img/org_menu_icon.webp'
import account_icon from '@/asset/img/account_icon.webp'
import {
  MenuIconType,
  AccountIcon,
  TicketIcon,
  InfoIcon,
  MemberIcon,
  NoticeIcon,
  OrderIcon,
  PermIcon,
  RoleIcon,
  ApiIcon,
  ShareIcon,
} from '@/icon/menu'
import ErrorFallBack from '@/layout/Error'

export interface RouteConfig extends NonIndexRouteObject {
  title?: string
  name?: string
  icon?: (v?: any) => React.ReactNode | React.ReactNode
  children?: RouteConfig[]
  permissions?: string[]
  hidden?: boolean
  hideMenu?: boolean
  showData?: string
}

const {
  Account,
  Login,
  Bind,
  LoginStarter,
  WorkbenchOld,
  App,
  Organization,
  OrderPage,
  MessagePage,
  Personal,
  Space,
  OrderPay,
  Workbench,
} = pages

const routes: RouteConfig[] = [
  {
    path: '/console',
    errorElement: <ErrorFallBack />,
    element: <Root />,
    children: [
      {
        path: 'personal/:id?',
        errorElement: <ErrorFallBack />,
        element: (
          <AuthComponent>
            <Personal />
          </AuthComponent>
        ),
      },
      {
        path: 'space/:id?',
        errorElement: <ErrorFallBack />,
        element: (
          <AuthComponent>
            <Space />
          </AuthComponent>
        ),
      },
      {
        path: 'order',
        errorElement: <ErrorFallBack />,
        element: (
          <AuthComponent>
            <OrderPay />
          </AuthComponent>
        ),
      },
      {
        path: 'starter',
        name: '第一步',
        errorElement: <ErrorFallBack />,
        element: (
          <AuthComponent>
            <LoginStarter />
          </AuthComponent>
        ),
      },
      {
        path: 'account',
        name: '个人中心',
        icon: () => <ImageIcon src={account_icon} sx={{ paddingTop: '20px', paddingLeft: '20px' }} />,
        errorElement: <ErrorFallBack />,
        element: (
          <AuthComponent>
            <Account />
          </AuthComponent>
        ),
        handle: {
          crumb: '个人中心',
        },
        children: [
          {
            path: '',
            hideMenu: true,
            element: <Navigate to='/console/personal/base' replace />,
          },
          {
            path: 'profile',
            name: '账号管理',
            icon: (type: MenuIconType) => <AccountIcon type={type} />,
            handle: {
              crumb: '账号管理',
            },
            children: [
              {
                path: '',
                hideMenu: true,
                element: <Navigate to='/console/personal/base' replace />,
              },
              {
                path: 'basic',
                name: '基本信息',
                errorElement: <ErrorFallBack />,
                element: <Navigate to='/console/personal/base' replace />,
                handle: {
                  crumb: '基本信息',
                },
              },
              {
                path: 'security',
                name: '安全设置',
                errorElement: <ErrorFallBack />,
                element: <Navigate to='/console/personal/base' replace />,
                handle: {
                  crumb: '安全设置',
                },
              },
              {
                path: 'verification',
                name: '个人实名认证',
                errorElement: <ErrorFallBack />,
                element: <Navigate to='/console/personal/base' replace />,
                handle: {
                  crumb: '个人实名认证',
                },
              },
            ],
          },
          {
            path: 'tickets',
            name: '工单管理',
            hideMenu: true,
            icon: (type: MenuIconType) => <TicketIcon type={type} />,
            element: <Account.Ticket />,
            handle: {
              crumb: '工单管理',
            },
            children: [
              {
                path: 'create',
                name: '提交工单',
                hideMenu: true,
                handle: {
                  crumb: '提交工单',
                },
              },
            ],
          },
          {
            path: 'share',
            name: '分享管理',
            icon: (type: MenuIconType) => <ShareIcon type={type} />,
            errorElement: <ErrorFallBack />,
            element: <Account.Share />,
          },
          {
            path: 'notifications',
            name: '消息',
            icon: (type: MenuIconType) => <NoticeIcon type={type} />,
            // permissions: ["admin", "alarm"],
            errorElement: <ErrorFallBack />,
            element: <Account.Notifications />,
            handle: {
              crumb: '消息',
            },
            children: [
              {
                path: 'history',
                name: '站内消息',
                showData: 'total_number',
                errorElement: <ErrorFallBack />,
                handle: {
                  crumb: '站内消息',
                },
              },
              {
                path: 'config',
                name: '消息通知',
                handle: {
                  crumb: '消息通知',
                },
                children: [
                  {
                    path: 'createConfig',
                    name: '创建消息转发',
                    hideMenu: true,
                    handle: {
                      crumb: '创建消息转发',
                    },
                  },
                  {
                    path: ':configId',
                    name: '修改消息转发',
                    hideMenu: true,
                    handle: {
                      crumb: '修改消息转发',
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'organization',
        name: '空间管理',
        icon: () => <ImageIcon src={org_menu_icon} sx={{ paddingTop: '20px', paddingLeft: '20px' }} />,
        errorElement: <ErrorFallBack />,
        element: (
          <AuthComponent needOrg={true}>
            <Organization />
          </AuthComponent>
        ),
        handle: {
          crumb: '空间管理',
        },
        children: [
          {
            path: '',
            hideMenu: true,
            element: <Navigate to='info' />,
          },
          {
            path: 'info',
            name: '信息',
            icon: (type: MenuIconType) => <InfoIcon type={type} />,
            errorElement: <ErrorFallBack />,
            element: <Organization.Info />,
            handle: {
              crumb: '信息',
            },
          },
          {
            path: 'member',
            name: '用户',
            permissions: ['admin'],
            icon: (type: MenuIconType) => <MemberIcon type={type} />,
            element: <Organization.Member />,
            handle: {
              crumb: '用户',
            },
            children: [
              {
                path: 'inviteMember',
                name: '邀请用户',
                hideMenu: true,
                handle: {
                  crumb: '邀请用户',
                },
              },
              {
                path: 'editMember',
                name: '修改用户角色',
                hideMenu: true,
                handle: {
                  crumb: '修改用户角色',
                },
              },
            ],
          },
          {
            path: 'role',
            name: '角色',
            icon: (type: MenuIconType) => <RoleIcon type={type} />,
            permissions: ['admin'],
            element: <Organization.Role />,
            handle: {
              crumb: '角色',
            },
            children: [
              {
                path: 'createRole',
                name: '创建角色',
                hideMenu: true,
                handle: {
                  crumb: '创建角色',
                },
              },
              {
                path: 'editRole',
                name: '修改角色权限',
                hideMenu: true,
                handle: {
                  crumb: '修改角色权限',
                },
              },
            ],
          },
          {
            path: 'permission',
            name: '权限',
            icon: (type: MenuIconType) => <PermIcon type={type} />,
            permissions: ['admin'],
            errorElement: <ErrorFallBack />,
            element: <Organization.Permission />,
            handle: {
              crumb: '权限',
            },
          },
          {
            path: 'api',
            name: 'API Token',
            permissions: ['admin'],
            icon: (type: MenuIconType) => <ApiIcon type={type} />,
            element: <Organization.APIToken />,
            handle: {
              crumb: 'API Token',
            },
            children: [
              {
                path: 'createToken',
                name: '生成API Token',
                hideMenu: true,
                handle: {
                  crumb: '生成API Token',
                },
              },
            ],
          },
          {
            path: 'orders',
            name: '订单管理',
            icon: (type: MenuIconType) => <OrderIcon type={type} />,
            permissions: ['admin'],
            element: <Organization.Orders />,
            handle: {
              crumb: '订单管理',
            },
            children: [
              {
                path: 'detail',
                name: '订单详情',
                permissions: ['admin'],
                hideMenu: true,
                errorElement: <ErrorFallBack />,
                element: <Organization.OrderDetail />,
                handle: {
                  crumb: '订单详情',
                },
              },
            ],
          },
        ],
      },
      {
        path: 'WorkbenchOld',
        name: '工作台',
        errorElement: <ErrorFallBack />,
        element: (
          <AuthComponent needOrg={true}>
            <WorkbenchOld />
          </AuthComponent>
        ),
      },
      {
        path: 'workbench',
        name: '工作台',
        errorElement: <ErrorFallBack />,
        element: (
          <AuthComponent needOrg={true}>
            <Workbench />
          </AuthComponent>
        ),
      },
      {
        path: 'app/:app_key',
        errorElement: <ErrorFallBack />,
        element: <App />,
        title: '应用',
        children: [
          {
            path: '*',
            element: null,
          },
        ],
      },
      // {
      //   path: 'order',
      //   errorElement: <ErrorFallBack />,
      //   element: (
      //     <AuthComponent>
      //       <OrderPage />
      //     </AuthComponent>
      //   ),
      // },
      {
        path: 'message/:message_id',
        errorElement: <ErrorFallBack />,
        element: (
          <AuthComponent>
            <MessagePage />
          </AuthComponent>
        ),
      },
      {
        path: '*',
        element: <Empty />,
      },
    ],
  },
  {
    path: '/500',
    element: <ErrorPage />,
  },
  {
    element: <PublicAuthLayout />,
    children: [
      {
        path: '/login',
        element: <Login />,
      },
      {
        path: '/register',
        element: <Login />,
      },
      {
        path: '/bind',
        element: <Bind />,
      },
    ],
  },
  {
    path: '/*',
    element: <Empty />,
  },
]

export const getPathName = (path: string, routes: RouteConfig[]): string | undefined => {
  const queue = []
  for (const item of routes) {
    if (item.path === path) {
      return item.name!
    }
    if (item.children) {
      queue.push(item.children)
    }
  }
  for (const item of queue) {
    const name = getPathName(path, item)
    if (name) {
      return name
    }
  }
}

export const getMenuItems = (root: string, routes: RouteConfig[]): RouteConfig | undefined => {
  const queue = []
  for (const item of routes) {
    if (item.path === root) {
      return item
    }
    if (item.children) {
      queue.push(item.children)
    }
  }
  for (const item of queue) {
    const items = getMenuItems(root, item)
    if (items) {
      return items
    }
  }
}

export default routes
